export enum MAIN_SPECIALIZATIONS {
  VOIX_CHANT = "voix-chant",
  DANSE = "danse",
  INSTRUMENT = "instrument",
  PREPARATION_PHYSIQUE_MENTALE = "preparation-physique-mentale",
}

// Max length of the free-text "Autre" specialization. Domain invariant, enforced identically by the student form,
// the admin review and the back-end value object.
export const OTHER_SPECIALIZATION_MAX = 80;
export const OTHER_SPECIALIZATION_MAX_ERROR = `La spécialité ne peut pas dépasser ${OTHER_SPECIALIZATION_MAX} caractères`;

type Leaf = { id: string; label: string };
type Group = { id: string; label: string; children: readonly Leaf[] };
type FlatNode = { kind: "flat"; label: string; emoji: string; children: readonly Leaf[] };
type GroupedNode = { kind: "grouped"; label: string; emoji: string; children: readonly Group[] };
type SpecializationNode = FlatNode | GroupedNode;

export const ORDERED_MAIN_SPECIALIZATIONS = [
  MAIN_SPECIALIZATIONS.VOIX_CHANT,
  MAIN_SPECIALIZATIONS.DANSE,
  MAIN_SPECIALIZATIONS.INSTRUMENT,
  MAIN_SPECIALIZATIONS.PREPARATION_PHYSIQUE_MENTALE,
] as const;

export const SPECIALIZATION_TREE = {
  [MAIN_SPECIALIZATIONS.VOIX_CHANT]: {
    label: "Voix / Chant",
    emoji: "🎤",
    kind: "grouped",
    children: [
      {
        id: "voix-chant.profil-metier",
        label: "Profil métier",
        children: [
          { id: "voix-chant.profil-metier.professeur-de-chant", label: "Professeur de Chant" },
          { id: "voix-chant.profil-metier.coach-vocal", label: "Coach Vocal" },
          { id: "voix-chant.profil-metier.pianiste-accompagnateur", label: "Pianiste Accompagnateur" },
          { id: "voix-chant.profil-metier.claveciniste-accompagnateur", label: "Claveciniste Accompagnateur" },
          { id: "voix-chant.profil-metier.chef-de-chant", label: "Chef de Chant" },
          { id: "voix-chant.profil-metier.chef-de-choeur", label: "Chef de Chœur" },
          { id: "voix-chant.profil-metier.coach-doublage-voix-off", label: "Coach doublage voix off" },
          { id: "voix-chant.profil-metier.metteur-en-scene", label: "Metteur en Scène" },
          { id: "voix-chant.profil-metier.coach-ecriture", label: "Coach d'écriture" },
          { id: "voix-chant.profil-metier.chef-orchestre", label: "Chef d'Orchestre" },
        ],
      },
      {
        id: "voix-chant.repertoires-styles",
        label: "Répertoires / styles",
        children: [
          { id: "voix-chant.repertoires-styles.opera-baroque", label: "Opéra Baroque" },
          { id: "voix-chant.repertoires-styles.opera-classique", label: "Opéra Classique" },
          { id: "voix-chant.repertoires-styles.opera-bel-canto", label: "Opéra Bel canto" },
          { id: "voix-chant.repertoires-styles.opera-romantique", label: "Opéra Romantique" },
          { id: "voix-chant.repertoires-styles.opera-verisme", label: "Opéra Vérisme" },
          { id: "voix-chant.repertoires-styles.opera-moderne-contemporain", label: "Opéra Moderne / Contemporain" },
          { id: "voix-chant.repertoires-styles.operette", label: "Opérette" },
          { id: "voix-chant.repertoires-styles.melodies-lieder", label: "Mélodies et Lieder" },
          { id: "voix-chant.repertoires-styles.oratorio", label: "Oratorio" },
          { id: "voix-chant.repertoires-styles.musique-contemporaine", label: "Musique contemporaine" },
          { id: "voix-chant.repertoires-styles.comedie-musicale", label: "Comédie musicale" },
          { id: "voix-chant.repertoires-styles.theatre-musical", label: "Théâtre musical" },
          {
            id: "voix-chant.repertoires-styles.musiques-actuelles",
            label: "Musiques actuelles (pop, rock, chanson, ...) - Voix amplifiées",
          },
          { id: "voix-chant.repertoires-styles.jazz-blues", label: "Jazz Blues" },
          { id: "voix-chant.repertoires-styles.repertoire-comediens", label: "Répertoire pour comédiens" },
        ],
      },
      {
        id: "voix-chant.travail-scenique",
        label: "Travail scénique",
        children: [
          { id: "voix-chant.travail-scenique.expression-scenique", label: "Expression scénique (chanteurs, danseurs, comédiens)" },
          { id: "voix-chant.travail-scenique.coaching-scenique-mise-en-scene", label: "Coaching scénique / mise en scène" },
          { id: "voix-chant.travail-scenique.seul-en-scene", label: "Seul en scène" },
          { id: "voix-chant.travail-scenique.direction-artistique-projet", label: "Direction artistique / projet" },
        ],
      },
      {
        id: "voix-chant.langues-voix-chantee",
        label: "Langues / travail sur l'interprétation voix chantée",
        children: [
          { id: "voix-chant.langues-voix-chantee.italien", label: "Italien" },
          { id: "voix-chant.langues-voix-chantee.allemand", label: "Allemand" },
          { id: "voix-chant.langues-voix-chantee.russe-langues-slaves", label: "Russe et langues slaves" },
          { id: "voix-chant.langues-voix-chantee.espagnol", label: "Espagnol" },
          { id: "voix-chant.langues-voix-chantee.anglais", label: "Anglais" },
          { id: "voix-chant.langues-voix-chantee.francais", label: "Français" },
          { id: "voix-chant.langues-voix-chantee.professeur-langue-native", label: "Professeur langue native" },
        ],
      },
      {
        id: "voix-chant.langues-voix-parlee",
        label: "Langues / travail sur l'interprétation voix parlée",
        children: [
          { id: "voix-chant.langues-voix-parlee.diction-lyrique", label: "Diction lyrique" },
          { id: "voix-chant.langues-voix-parlee.diction-baroque", label: "Diction baroque" },
          { id: "voix-chant.langues-voix-parlee.travail-texte-parle-recitatifs", label: "Travail du texte parlé / des récitatifs" },
          { id: "voix-chant.langues-voix-parlee.travail-declamation", label: "Travail sur la déclamation" },
        ],
      },
      {
        id: "voix-chant.divers",
        label: "Divers",
        children: [
          { id: "voix-chant.divers.solfege", label: "Apprentissage du solfège" },
          { id: "voix-chant.divers.dechiffrage-partitions", label: "Déchiffrage partitions pour chanteur" },
          { id: "voix-chant.divers.autonomie-piano-chanteur", label: "S'accompagner au piano, autonomie du chanteur" },
          { id: "voix-chant.divers.ecriture-musicale", label: "Professeur d'écriture musicale" },
          { id: "voix-chant.divers.formation-formateur", label: "Formation de formateur" },
          { id: "voix-chant.divers.certifie-tcm", label: "Professeur certifié TCM" },
          { id: "voix-chant.divers.handicap", label: "Enseignement pour artistes en situation de handicap" },
        ],
      },
    ],
  },
  [MAIN_SPECIALIZATIONS.DANSE]: {
    label: "Danse",
    emoji: "💃",
    kind: "flat",
    children: [
      { id: "danse.classique", label: "Danse classique" },
      { id: "danse.contemporaine", label: "Danse contemporaine" },
      { id: "danse.jazz", label: "Danse jazz" },
      { id: "danse.comedie-musicale", label: "Danse comédie musicale" },
      { id: "danse.hip-hop-urbaines", label: "Hip-hop / danses urbaines" },
      { id: "danse.baroque", label: "Danse baroque" },
      { id: "danse.claquettes", label: "Claquettes" },
      { id: "danse.preparation-auditions-concours", label: "Préparation aux auditions et concours" },
    ],
  },
  [MAIN_SPECIALIZATIONS.INSTRUMENT]: {
    label: "Instrument",
    emoji: "🎹",
    kind: "flat",
    children: [
      { id: "instrument.piano", label: "Piano" },
      { id: "instrument.cordes-frottees", label: "Cordes frottées (violon, alto, violoncelle, contrebasse)" },
      { id: "instrument.cordes-pincees", label: "Cordes pincées (guitare, harpe, luth)" },
      { id: "instrument.vents-bois", label: "Vents bois (flûtes, hautbois, clarinette, basson, saxophone)" },
      { id: "instrument.vents-cuivres", label: "Vents cuivres (trompette, cor, trombone, tuba)" },
      { id: "instrument.percussions-batterie", label: "Percussions et batterie" },
      { id: "instrument.claviers-anciens", label: "Claviers anciens (clavecin, orgue)" },
      { id: "instrument.direction-orchestre-ensemble", label: "Direction d'orchestre / direction d'ensemble" },
    ],
  },
  [MAIN_SPECIALIZATIONS.PREPARATION_PHYSIQUE_MENTALE]: {
    label: "Préparation physique et mentale de l'artiste",
    emoji: "🧘",
    kind: "flat",
    children: [
      { id: "preparation-physique-mentale.technique-alexander", label: "Technique Alexander" },
      { id: "preparation-physique-mentale.pnl", label: "PNL" },
      { id: "preparation-physique-mentale.hypnose", label: "Hypnose" },
      {
        id: "preparation-physique-mentale.travail-corporel-respiratoire",
        label: "Travail corporel et respiratoire pour chanteurs / Gestion du souffle",
      },
      { id: "preparation-physique-mentale.soins-voix-corps", label: "Soins Voix et Corps (Shiatsu, Yoga, Kinésiologie)" },
      { id: "preparation-physique-mentale.coaching-accompagnement-mental", label: "Coaching / Accompagnement / Préparation mentale" },
    ],
  },
} as const satisfies Record<MAIN_SPECIALIZATIONS, SpecializationNode>;

type Tree = typeof SPECIALIZATION_TREE;
type AnyNode = Tree[keyof Tree];
type FlatLeafId = Extract<AnyNode, { kind: "flat" }>["children"][number]["id"];
type GroupedLeafId = Extract<AnyNode, { kind: "grouped" }>["children"][number]["children"][number]["id"];
export type LeafId = FlatLeafId | GroupedLeafId;

export const LEAF_IDS: ReadonlySet<LeafId> = new Set(
  Object.values(SPECIALIZATION_TREE).flatMap(node =>
    node.kind === "flat" ? node.children.map(l => l.id) : node.children.flatMap(g => g.children.map(l => l.id)),
  ),
);

export const ALL_LEAF_IDS: readonly LeafId[] = Array.from(LEAF_IDS);

export const getMainOf = (leafId: LeafId): MAIN_SPECIALIZATIONS => leafId.split(".")[0] as MAIN_SPECIALIZATIONS;

export const getLeafLabel = (id: LeafId): string => {
  for (const node of Object.values(SPECIALIZATION_TREE)) {
    if (node.kind === "flat") {
      const leaf = node.children.find(l => l.id === id);
      if (leaf) return leaf.label;
    } else {
      for (const group of node.children) {
        const leaf = group.children.find(l => l.id === id);
        if (leaf) return leaf.label;
      }
    }
  }
  throw new Error(`Unknown leaf id: ${id}`);
};
